/* v8 ignore file */
import { Chart } from "@appretio/ui-kit";
import { AgNodeClickEvent } from "ag-charts-enterprise";
import { useCallback, useEffect, useState } from "react";
import {
  ColourMetrics,
  GroupedColourMetrics,
  KeysWithValsOfType
} from "../../../pages/inSeasonReviewPage/hooks/useFetchColours";
import { chartWrapper } from "../Chart.css";
import { useColourChartOptions } from "./hooks/useColourChartOptions";
import { groupByColourParent } from "./util/groupByColourParent";

interface ColourChartProps {
  /**
   * The flat colour metrics data to show in the chart.
   */
  data: ColourMetrics[];
  /**
   * Loading state
   */
  isLoading: boolean;
  /**
   * The key of ColourMetrics to use for sizing the sectors and showing as value.
   */
  valueKey: KeysWithValsOfType<ColourMetrics, number>;
  /**
   * Optional title for the chart.
   */
  title?: string;
}

/**
 * Colour sunburst chart component.
 * Shows either sales or purchases grouped by colour hex code.
 * Aggregates and groups server side for initial query (full data set), and client side for selected rows.
 */
export function ColourChart({
  data,
  valueKey,
  isLoading,
  title
}: ColourChartProps) {
  const [selected, setSelected] = useState<string>();

  const { series } = useColourChartOptions({
    valueKey
  });

  const filtered = selected
    ? data.filter((item) => item.parent === selected)
    : data;

  const grouped = groupByColourParent(filtered, valueKey);

  const handleNodeClick = useCallback(
    (
      event: AgNodeClickEvent<"seriesNodeClick", GroupedColourMetrics, unknown>
    ) => {
      const datum = event.datum;

      if (!datum || selected === datum.name) {
        setSelected(undefined);
      } else if (datum.children) {
        setSelected(datum.name);
      }
    },
    [selected]
  );

  useEffect(() => {
    setSelected(undefined);
  }, [data]);

  return (
    <div className={chartWrapper}>
      <Chart
        isLoading={isLoading}
        options={{
          listeners: {
            seriesNodeClick: handleNodeClick
          },
          data: grouped,
          padding: {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0
          },
          animation: { enabled: true },
          title: title ? { text: title, textAlign: "center" } : undefined,
          series,
          tooltip: {
            pagination: false
          }
        }}
      />
    </div>
  );
}
